<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import DropeeSpinnerIcon from '@/components/icons/DropeeSpinnerIcon.vue'
import { computed, ref, watch } from 'vue'
import { useMultiplayerMine } from '@/composables/game/multiplayer-mine'
import { usePlayer } from '@/composables/game/player'
import mineImage from '@/assets/images/special-currencies/mine.webp'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  hide: []
}>()

const loading = ref(false)
const { openMineTutorialModal, hasSeenMineTutorial, plantMine } = useMultiplayerMine()
const { player } = usePlayer()
const availableMines = computed(() => (player.value.specialCurrencies as Record<string, number>)['mp.mine'] ?? 0)

const selectedCount = ref(1)

watch(() => props.show, (val) => {
  if (val) {
    selectedCount.value = 1
    if (!hasSeenMineTutorial.value) {
      openMineTutorialModal()
      emit('hide')
      return
    }
  }
})

function increment() {
  if (selectedCount.value < availableMines.value) selectedCount.value++
}

function decrement() {
  if (selectedCount.value > 1) selectedCount.value--
}

async function onConfirm() {
  loading.value = true
  try {
    await plantMine(selectedCount.value)
  } catch (e) {
    console.error('Failed to plant mine', e)
  } finally {
    loading.value = false
    emit('hide')
  }
}
</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-white/[.01] backdrop-blur-[12px] flex flex-col items-center justify-center z-[800] px-6"
    >
      <div class="pulsating-border" />
      <template v-if="loading">
        <DropeeSpinnerIcon class="w-12 h-12 text-white" />
      </template>
      <template v-else>
        <div class="relative z-10 w-full flex flex-col items-center max-w-[400px]">
          <h2 class="title uppercase font-pilat text-[32px] leading-[38px] font-black text-white tracking-tight text-center mb-6">
            READY TO ATTACK?
          </h2>
          <p class="subtitle text-white text-base font-semibold text-center mb-3">
            Plant mine{{ availableMines === 1 ? '' : 's' }} on another player card. If they upgrade it, you steal coins from them.
          </p> 
          <p class="subtitle text-white/60 text-sm text-center mb-8">
            You have <span class="text-white/[.87]">{{ availableMines }}</span> mine{{ availableMines === 1 ? '' : 's' }} available.
          </p>
          <div class="flex items-center w-full mb-8">
            <button
              class="gradient-border w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold disabled:opacity-40"
              :disabled="selectedCount === 1"
              @click="decrement"
            >
              -
            </button>
            <div class="gradient-border relative flex-1 mx-2 rounded-xl flex flex-col items-center justify-center py-4 overflow-hidden">
              <img
                :src="mineImage"
                alt="mine"
                class="w-20 h-20 object-contain mb-2"
              >
              <span class="text-white text-3xl font-black">{{ selectedCount }}</span>
            </div>
            <button
              class="gradient-border w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold disabled:opacity-40"
              :disabled="selectedCount === availableMines"
              @click="increment"
            >
              +
            </button>
          </div>
          <BaseButton
            class="w-full mb-3"
            :disabled="loading"
            @click="onConfirm"
          >
            PLANT {{ selectedCount === 1 ? 'MINE' : 'MINES' }}
          </BaseButton>
          <BaseButton
            class="w-full"
            style-type="secondary"
            @click="$emit('hide')"
          >
            CANCEL
          </BaseButton>
        </div>
      </template>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.title {
  text-shadow: 0 0 16px #000;
  -webkit-text-stroke-width: 1px;
  -webkit-text-stroke-color: #000;
}
.subtitle {
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.88);
}

.gradient-border {
  border: 2px solid transparent;
  background-image:
    linear-gradient(#1d1d1d, #1d1d1d),
    linear-gradient(to bottom, rgba(98, 98, 98, 0.66), transparent);
  background-origin: border-box;
  background-clip: padding-box, border-box;
}

.pulsating-border {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 22px solid #F00;
  filter: blur(20.6px);
  animation: border-pulse 920ms ease-out infinite;
}

@keyframes border-pulse {
  0%, 100% {
    border-width: 22px;
    filter: blur(20.6px);
  }
  50% {
    border-width: 55px;
    filter: blur(32.45px);
  }
}
</style>
